import { Heading } from '@zopauk/web-ui';
import { StructuredText } from 'react-datocms';
import { buildModule } from '@/components/Modules';
import { Band } from '@/components/Modules/Band/Band';
import type { Document } from 'datocms-structured-text-utils';
import { customNodeRules } from '@/components/structuredText';
import type { LandingPageFaqAccordionFragment } from '@/utils/dato/generated';

// `inArticle` is set by the blog article body: the article column already constrains the width, so the module
// fills it (same width as the surrounding text) and skips the Band wrapper's page-level spacing
type LandingPageFaqAccordionProps = { pageContext?: { inArticle?: boolean } } & LandingPageFaqAccordionFragment;

const LandingPageFaqAccordion = ({ pageContext, header, button, faqs }: LandingPageFaqAccordionProps) => {
  const inArticle = Boolean(pageContext?.inArticle);

  const content = (
    <>
      {header ? (
        <Heading className="my-4 font-serif font-semibold mb-6 lg:mb-7 text-left lg:text-center" as="h2">
          {header}
        </Heading>
      ) : null}
      <div aria-label="FAQ" role="list">
        {faqs.map(({ question, answer, id }, index) =>
          answer ? (
            <div className={index + 1 !== faqs.length ? 'mb-2' : ''} role="listitem" key={id}>
              <details className="group">
                <summary className="list-none cursor-pointer">
                  <div className="flex items-center justify-between px-6 py-4 bg-base-100">
                    <span>{question}</span>
                    <span className="text-xl ml-4 shrink-0">
                      <span className="group-open:hidden">+</span>
                      <span className="hidden group-open:inline">–</span>
                    </span>
                  </div>
                </summary>
                <div className="py-[15px]">
                  <StructuredText data={{ value: answer.value as Document }} customNodeRules={customNodeRules} />
                </div>
              </details>
            </div>
          ) : null,
        )}
      </div>
      {button[0] ? <div className="mt-8 lg:mt-10 flex justify-center">{buildModule({ module: button[0] })}</div> : null}
    </>
  );

  if (inArticle) {
    return (
      <div data-automation="ZA.landingPageFaqAccordion-article" className="w-full my-8">
        {content}
      </div>
    );
  }

  return (
    <Band data-automation="ZA.landingPageFaqAccordion-band">
      <div className="flex justify-center">
        <div className="w-full lg:w-8/12 xl:w-6/12">{content}</div>
      </div>
    </Band>
  );
};

export default LandingPageFaqAccordion;
